import {
	Box,
	Button,
	CircularProgress,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useApi } from '../../../../../hooks/useApi';
import {
	useCompanyDispatch,
	useCompanyState,
} from '../../../../../stateProviders/CompanyStateProvider';
import { useUnsavedFormChanges } from '../../../../../hooks/useUnsavedFormChanges';
import { useUnsavedChangesContext } from '../../../../../contexts/UnsavedChangesContext';

export const BaseLogisticFees = () => {
	const api = useApi();
	const companyState = useCompanyState();
	const companyDispatch = useCompanyDispatch();
	const { companyId } = useParams();

	const [salesTaxPercentage, setSalesTaxPercentage] = useState(
		String(companyState.logisticCosts?.salesTaxPercentage || '')
	);
	const [dumpsterCost, setDumpsterCost] = useState(
		String(companyState.logisticCosts?.dumpsterCost || '')
	);
	const [engineeringFee, setEngineeringFee] = useState(
		String(companyState.logisticCosts?.engineeringFee || '')
	);
	const [drawingCost, setDrawingCost] = useState(
		String(companyState.logisticCosts?.drawingCost || '')
	);
	const [septicPermitCost, setSepticPermitCost] = useState(
		String(companyState.logisticCosts?.septicPermitCost || '')
	);
	const [sitePlanCost, setSitePlanCost] = useState(
		String(companyState.logisticCosts?.sitePlanCost || '')
	);
	const [permitAdminFee, setPermitAdminFee] = useState(
		String(companyState.logisticCosts?.permitAdminFee || '')
	);

	const [isSavingLogisticCosts, setIsSavingLogisticCosts] = useState(false);

	useEffect(() => {
		if (companyState.logisticCosts) {
			setSalesTaxPercentage(
				String(companyState.logisticCosts.salesTaxPercentage || 0)
			);
			setDumpsterCost(String(companyState.logisticCosts.dumpsterCost || 0));
			setEngineeringFee(String(companyState.logisticCosts.engineeringFee || 0));
			setDrawingCost(String(companyState.logisticCosts.drawingCost || 0));
			setSepticPermitCost(
				String(companyState.logisticCosts.septicPermitCost || 0)
			);
			setSitePlanCost(String(companyState.logisticCosts.sitePlanCost || 0));
			setPermitAdminFee(String(companyState.logisticCosts.permitAdminFee || 0));
		}
	}, [companyState.logisticCosts]);

	const savedFormData = useMemo(
		() => ({
			salesTaxPercentage: String(
				companyState.logisticCosts?.salesTaxPercentage || 0
			),
			dumpsterCost: String(companyState.logisticCosts?.dumpsterCost || 0),
			engineeringFee: String(companyState.logisticCosts?.engineeringFee || 0),
			drawingCost: String(companyState.logisticCosts?.drawingCost || 0),
			septicPermitCost: String(
				companyState.logisticCosts?.septicPermitCost || 0
			),
			sitePlanCost: String(companyState.logisticCosts?.sitePlanCost || 0),
			permitAdminFee: String(companyState.logisticCosts?.permitAdminFee || 0),
		}),
		[companyState.logisticCosts]
	);

	const currentFormData = useMemo(
		() => ({
			salesTaxPercentage,
			dumpsterCost,
			engineeringFee,
			drawingCost,
			septicPermitCost,
			sitePlanCost,
			permitAdminFee,
		}),
		[
			salesTaxPercentage,
			dumpsterCost,
			engineeringFee,
			drawingCost,
			septicPermitCost,
			sitePlanCost,
			permitAdminFee,
		]
	);

	const { isDirty, markAsClean } = useUnsavedFormChanges(
		currentFormData,
		savedFormData
	);
	const { setHasUnsavedChanges, setOnSaveCallback } =
		useUnsavedChangesContext();

	const handleDecimalChange =
		(setter: (value: string) => void) =>
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setter(e.target.value);
		};

	const handleIntegerChange =
		(setter: (value: string) => void) =>
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setter(e.target.value);
		};

	const handleSaveLogisticCosts = async () => {
		if (!companyState.logisticCosts?._id) return;
		setIsSavingLogisticCosts(true);
		try {
			await api.logisticCosts.update(companyState.logisticCosts._id, {
				salesTaxPercentage: parseFloat(salesTaxPercentage) || 0,
				dumpsterCost: parseFloat(dumpsterCost) || 0,
				engineeringFee: parseFloat(engineeringFee) || 0,
				drawingCost: parseFloat(drawingCost) || 0,
				septicPermitCost: parseFloat(septicPermitCost) || 0,
				sitePlanCost: parseFloat(sitePlanCost) || 0,
				permitAdminFee: parseFloat(permitAdminFee) || 0,
			});
			const updatedLogisticCosts = await api.logisticCosts.find({ companyId });
			companyDispatch({ logisticCosts: updatedLogisticCosts });
			markAsClean();
		} catch (error) {
		} finally {
			setIsSavingLogisticCosts(false);
		}
	};

	useEffect(() => {
		setHasUnsavedChanges(isDirty);
	}, [isDirty, setHasUnsavedChanges]);

	useEffect(() => {
		if (isDirty) {
			setOnSaveCallback(() => async () => {
				await handleSaveLogisticCosts();
			});
		} else {
			setOnSaveCallback(null);
		}
	}, [
		isDirty,
		salesTaxPercentage,
		dumpsterCost,
		engineeringFee,
		drawingCost,
		septicPermitCost,
		sitePlanCost,
		permitAdminFee,
	]);

	useEffect(() => {
		return () => {
			setHasUnsavedChanges(false);
			setOnSaveCallback(null);
		};
	}, [setHasUnsavedChanges, setOnSaveCallback]);

	return (
		<Box display="flex" flexDirection="column" height="100%">
			<Box
				sx={{
					position: 'sticky',
					top: 0,
					backgroundColor: 'background.default',
					zIndex: 10,
					pb: 2,
					borderBottom: '1px solid',
					borderColor: 'divider',
					mb: 3,
				}}
			>
				<Stack spacing={1}>
					<Typography variant="h2" fontWeight={700}>
						Base Logistic Costs
					</Typography>
					<Typography variant="subtitle1" color="text.secondary">
						Set pricing on base logistics fees that can be applied to quotes
					</Typography>
				</Stack>
			</Box>

			<Box
				sx={{
					flex: 1,
					overflow: 'auto',
					display: 'flex',
					flexDirection: 'column',
					rowGap: { xs: 3, sm: 4 },
				}}
			>
				<Box
					display="grid"
					gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
					gap={2.5}
				>
					<Box>
						<Typography variant="subtitle2" sx={{ mb: 1 }}>
							Sales Tax %
						</Typography>
						<TextField
							fullWidth
							type="number"
							value={salesTaxPercentage}
							onChange={handleDecimalChange(setSalesTaxPercentage)}
							slotProps={{ htmlInput: { step: '0.01', inputMode: 'decimal' } }}
							data-testid="sales-tax-input"
						/>
					</Box>
					<Box>
						<Typography variant="subtitle2" sx={{ mb: 1 }}>
							Dumpster ($)
						</Typography>
						<TextField
							fullWidth
							type="number"
							value={dumpsterCost}
							onChange={handleIntegerChange(setDumpsterCost)}
							slotProps={{ htmlInput: { step: 1, inputMode: 'numeric' } }}
							data-testid="dumpster-cost-input"
						/>
					</Box>
					<Box>
						<Typography variant="subtitle2" sx={{ mb: 1 }}>
							Engineering Fees ($)
						</Typography>
						<TextField
							fullWidth
							type="number"
							value={engineeringFee}
							onChange={handleIntegerChange(setEngineeringFee)}
							slotProps={{ htmlInput: { step: 1, inputMode: 'numeric' } }}
							data-testid="engineering-fee-input"
						/>
					</Box>
					<Box>
						<Typography variant="subtitle2" sx={{ mb: 1 }}>
							Deck Drawings ($)
						</Typography>
						<TextField
							fullWidth
							type="number"
							value={drawingCost}
							onChange={handleIntegerChange(setDrawingCost)}
							slotProps={{ htmlInput: { step: 1, inputMode: 'numeric' } }}
							data-testid="deck-drawings-input"
						/>
					</Box>
					<Box>
						<Typography variant="subtitle2" sx={{ mb: 1 }}>
							Septic Permit ($)
						</Typography>
						<TextField
							fullWidth
							type="number"
							value={septicPermitCost}
							onChange={handleIntegerChange(setSepticPermitCost)}
							slotProps={{ htmlInput: { step: 1, inputMode: 'numeric' } }}
							data-testid="septic-permit-input"
						/>
					</Box>
					<Box>
						<Typography variant="subtitle2" sx={{ mb: 1 }}>
							Site Plan ($)
						</Typography>
						<TextField
							fullWidth
							type="number"
							value={sitePlanCost}
							onChange={handleIntegerChange(setSitePlanCost)}
							slotProps={{ htmlInput: { step: 1, inputMode: 'numeric' } }}
							data-testid="site-plan-input"
						/>
					</Box>
					<Box>
						<Typography variant="subtitle2" sx={{ mb: 1 }}>
							Permit Admin Fee ($)
						</Typography>
						<TextField
							fullWidth
							type="number"
							value={permitAdminFee}
							onChange={handleIntegerChange(setPermitAdminFee)}
							slotProps={{ htmlInput: { step: 1, inputMode: 'numeric' } }}
							data-testid="permit-admin-fee-input"
						/>
					</Box>
				</Box>
			</Box>
			<Box sx={{ mt: 3 }}>
				<Button
					variant="contained"
					color="success"
					disabled={isSavingLogisticCosts}
					onClick={handleSaveLogisticCosts}
				>
					{isSavingLogisticCosts ? (
						<Box display="flex" alignItems="center" gap={1}>
							<CircularProgress size={18} color="success" />
							<Typography variant="xs" component="span" fontWeight={600}>
								Saving...
							</Typography>
						</Box>
					) : (
						'Save Base Logistic Costs'
					)}
				</Button>
			</Box>
		</Box>
	);
};
